import { create } from 'zustand';
import { fetchWithAuth } from '@/services/api';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';

export interface CannedResponse {
  id: string;
  tenant_id?: string | null;
  loja_id?: string | null;
  shortcut: string;
  content: string;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface CannedResponsesState {
  cannedResponses: CannedResponse[];
  loading: boolean;
  fetched: boolean;
  fetchCannedResponses: (force?: boolean) => Promise<void>;
  createCannedResponse: (shortcut: string, content: string) => Promise<boolean>;
  updateCannedResponse: (id: string, shortcut: string, content: string) => Promise<boolean>;
  deleteCannedResponse: (id: string) => Promise<boolean>;
}

export const useCannedResponsesStore = create<CannedResponsesState>((set, get) => ({
  cannedResponses: [],
  loading: false,
  fetched: false,

  fetchCannedResponses: async (force = false) => {
    if (get().fetched && !force && get().cannedResponses.length > 0) return;

    set({ loading: true });
    const lojaAtivaId = useLojaAtivaStore.getState().lojaAtivaId;

    try {
      const url = `/api/canned-responses${lojaAtivaId ? `?loja_id=${lojaAtivaId}` : ''}`;
      const res = await fetchWithAuth(url);
      if (res.ok) {
        const data = await res.json();
        set({ cannedResponses: Array.isArray(data) ? (data as CannedResponse[]) : [], fetched: true });
      } else {
        set({ cannedResponses: [], fetched: true });
      }
    } catch (err) {
      console.warn('[CANNED_RESPONSES_STORE] Express API fetch warning:', err);
      set({ cannedResponses: [], fetched: true });
    } finally {
      set({ loading: false });
    }
  },

  createCannedResponse: async (shortcut: string, content: string) => {
    try {
      let formatted = shortcut.trim();
      if (!formatted.startsWith('/')) {
        formatted = '/' + formatted;
      }
      const lojaAtivaId = useLojaAtivaStore.getState().lojaAtivaId;

      const res = await fetchWithAuth('/api/canned-responses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortcut: formatted, content: content.trim(), loja_id: lojaAtivaId || null })
      });

      if (res.ok) {
        const data = await res.json();
        set(state => ({
          cannedResponses: [...state.cannedResponses, data as CannedResponse]
        }));
        return true;
      }
    } catch (err) {
      console.error('[CANNED_RESPONSES_STORE] Error creating:', err);
    }
    return false;
  },

  updateCannedResponse: async (id: string, shortcut: string, content: string) => {
    try {
      let formatted = shortcut.trim();
      if (!formatted.startsWith('/')) {
        formatted = '/' + formatted;
      }
      const res = await fetchWithAuth(`/api/canned-responses/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortcut: formatted, content: content.trim() })
      });

      if (res.ok) {
        set(state => ({
          cannedResponses: state.cannedResponses.map(c =>
            c.id === id ? { ...c, shortcut: formatted, content: content.trim() } : c
          )
        }));
        return true;
      }
    } catch (err) {
      console.error('[CANNED_RESPONSES_STORE] Error updating:', err);
    }
    return false;
  },

  deleteCannedResponse: async (id: string) => {
    try {
      const res = await fetchWithAuth(`/api/canned-responses/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        set(state => ({
          cannedResponses: state.cannedResponses.filter(c => c.id !== id)
        }));
        return true;
      }
    } catch (err) {
      console.error('[CANNED_RESPONSES_STORE] Error deleting:', err);
    }
    return false;
  }
}));
